import React, { useEffect, useMemo, useRef, useState } from "react";
import type { ArtistDTO, SetDTO, StageDTO } from "../lib/api";
import type { PreferenceMap, Rating } from "../lib/preferences";
import { IconFilter, IconSearch, IconX } from "../components/icons";
import { FilterSheet, dayLabel, EMPTY_FILTER, type BrowseFilter } from "../components/FilterSheet";
import { dayLabels } from "../lib/format";

/** What to order each group's items by. "stage" is intentionally absent —
 *  if you want a stage cut, group by stage instead (avoids two segmented-
 *  control tabs sharing the same label and confusing the e2e selectors). */
type SortKey = "time" | "name" | "genre";
type GroupKey = "none" | "day" | "stage" | "genre";

export function Browse({
  stages,
  genres,
  onOpen,
  artists,
  loading,
  prefs,
  eventName,
  eventLocation,
  eventStartDate,
  eventEndDate,
  onChangeFilter,
}: {
  stages: StageDTO[];
  genres: string[];
  artists: ArtistDTO[];
  loading: boolean;
  prefs: PreferenceMap;
  onOpen: (slug: string | null) => void;
  /** Parent event's name — shown in the "super" label above the header. */
  eventName?: string | null;
  /** Parent event's location — appended after the dates. */
  eventLocation?: string | null;
  /** Used to label grouped-by-day sections + the header with real event dates. */
  eventStartDate?: string | null;
  eventEndDate?: string | null;
  onChangeFilter: (f: BrowseFilter & { q?: string }) => void;
}) {
  const [sort, setSort] = useState<SortKey>("name");
  const [group, setGroup] = useState<GroupKey>("none");
  // Tracks whether the user has explicitly picked a sort. Until they do,
  // the smart default below auto-flips between "name" and "time" based on
  // whether any day filter is active. Once they pick, we respect their
  // choice across all subsequent filter changes.
  const sortPinned = useRef(false);
  const [filter, setFilter] = useState<BrowseFilter>(EMPTY_FILTER);
  const [q, setQ] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);

  // Smart default: a day filter implies the user is planning their day,
  // so chronological order is the obvious sort. Removing the day filter
  // pops back to alphabetical. Bypassed once the user picks explicitly.
  useEffect(() => {
    if (sortPinned.current) return;
    setSort(filter.days.length > 0 ? "time" : "name");
  }, [filter.days]);

  function pickSort(s: SortKey) {
    sortPinned.current = true;
    setSort(s);
  }

  // Debounce the query and propagate filter changes upward.
  useEffect(() => {
    const handler = setTimeout(() => {
      onChangeFilter({
        days: filter.days,
        stages: filter.stages,
        genres: filter.genres,
        q: q.trim() || undefined,
      });
    }, 150);
    return () => clearTimeout(handler);
  }, [filter, q, onChangeFilter]);

  // Some events haven't published real timetables yet (sets stored with
  // ordinal start=end=N). Hide the "time" sort pill in that case so we
  // don't confuse users with a no-op order.
  const hasRealTimes = useMemo(
    () => artists.some(a => a.sets.some(s => s.end > s.start && s.startLabel !== "")),
    [artists],
  );
  // If we just lost real-time data (e.g. user switched events), drop
  // "time" sort so the visible state stays coherent.
  useEffect(() => {
    if (!hasRealTimes && sort === "time") setSort("name");
  }, [hasRealTimes, sort]);

  const grouped = useMemo(
    () =>
      sortAndGroup(artists, {
        sort,
        group,
        stages,
        eventStartDate: eventStartDate ?? null,
        filterDays: filter.days,
        filterStages: filter.stages,
      }),
    [artists, sort, group, stages, eventStartDate, filter.days, filter.stages],
  );
  const activeCount = filter.days.length + filter.stages.length + filter.genres.length;
  // Max day number across all sets — determines how many day chips to render.
  const maxDay = useMemo(
    () => artists.reduce((m, a) => a.sets.reduce((mm, s) => Math.max(mm, s.day), m), 0),
    [artists],
  );
  const stageByslug = useMemo(
    () => new Map(stages.map(s => [s.slug, s])),
    [stages],
  );

  const removeDay = (d: number) =>
    setFilter(prev => ({ ...prev, days: prev.days.filter(x => x !== d) }));
  const removeStage = (s: string) =>
    setFilter(prev => ({ ...prev, stages: prev.stages.filter(x => x !== s) }));
  const removeGenre = (g: string) =>
    setFilter(prev => ({ ...prev, genres: prev.genres.filter(x => x !== g) }));

  // Build the per-event super label:
  //   "{NAME} · {DATE RANGE} · {LOCATION}"
  // Each bit is optional; falls back to "THE LINEUP" when we have nothing.
  // The .super CSS truncates with ellipsis on narrow screens so the row
  // never wraps.
  const superLabel = useMemo(() => {
    const bits: string[] = [];
    if (eventName) bits.push(eventName.toUpperCase());
    if (eventStartDate) {
      const range = shortDateRange(eventStartDate, eventEndDate ?? eventStartDate);
      if (range) bits.push(range);
    }
    if (eventLocation) bits.push(eventLocation.toUpperCase());
    return bits.length > 0 ? bits.join(" · ") : "THE LINEUP";
  }, [eventName, eventLocation, eventStartDate, eventEndDate]);

  return (
    <div data-testid="view-browse">
      <div className="section-head">
        <div>
          <span className="super">{superLabel}</span>
          <h2>The Lineup</h2>
        </div>
      </div>

      {/* Search + Filter button */}
      <div className="browse-toolbar">
        <label className="search">
          <IconSearch />
          <input
            aria-label="Search artists"
            placeholder="search artists"
            value={q}
            onChange={e => setQ(e.target.value)}
            data-testid="search-input"
          />
          {q && (
            <button
              aria-label="Clear search"
              onClick={() => setQ("")}
              className="search-clear"
              data-testid="search-clear"
            >
              <IconX />
            </button>
          )}
        </label>
        <button
          className={`filter-btn ${activeCount ? "on" : ""}`}
          onClick={() => setSheetOpen(true)}
          data-testid="open-filters"
        >
          <IconFilter />
          <span>Filter</span>
          {activeCount > 0 && (
            <span className="filter-badge">{activeCount}</span>
          )}
        </button>
      </div>

      {/* Active-filter chips — one per selected value */}
      {activeCount > 0 && (
        <div className="chiprow" data-testid="active-filters">
          {filter.days.map(d => {
            const labels = eventStartDate ? dayLabels(eventStartDate, 14) : null;
            const text = labels?.[d - 1] ?? `Day ${d}`;
            return (
              <button
                key={`d${d}`}
                className="chip on"
                onClick={() => removeDay(d)}
                data-testid={`active-filter-day-${d}`}
              >
                {text}
                <IconX />
              </button>
            );
          })}
          {filter.stages.map(s => (
            <button
              key={`s${s}`}
              className="chip on"
              onClick={() => removeStage(s)}
              data-testid={`active-filter-stage-${s}`}
              style={{
                borderColor: stageByslug.get(s)?.color ?? undefined,
                color: stageByslug.get(s)?.color ?? undefined,
              }}
            >
              {stageByslug.get(s)?.name ?? s}
              <IconX />
            </button>
          ))}
          {filter.genres.map(g => (
            <button
              key={`g${g}`}
              className="chip on"
              onClick={() => removeGenre(g)}
              data-testid={`active-filter-genre-${g}`}
            >
              {g}
              <IconX />
            </button>
          ))}
          <button
            className="chip"
            onClick={() => setFilter(EMPTY_FILTER)}
            style={{ opacity: 0.7 }}
            data-testid="clear-all-filters"
          >
            clear all
          </button>
        </div>
      )}

      {/* Sort + grouping pills (two independent axes) */}
      <div className="sort-row">
        <span className="sort-label">Sort</span>
        <div className="seg" role="tablist" aria-label="Sort by">
          {((hasRealTimes ? ["time", "name", "genre"] : ["name", "genre"]) as SortKey[]).map(s => (
            <button
              key={s}
              role="tab"
              aria-pressed={sort === s}
              onClick={() => pickSort(s)}
              data-testid={`sort-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
      <div className="sort-row">
        <span className="sort-label">Group</span>
        <div className="seg" role="tablist" aria-label="Group by">
          {(["none", "day", "stage", "genre"] as GroupKey[]).map(g => (
            <button
              key={g}
              role="tab"
              aria-pressed={group === g}
              onClick={() => setGroup(g)}
              data-testid={`group-${g}`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="empty">Loading artists…</div>}
      {!loading && artists.length === 0 && (
        <div className="empty">
          <div className="emoji">🎛️</div>
          No artists match those filters.
          {activeCount > 0 && (
            <div style={{ marginTop: 10 }}>
              <button className="chip" onClick={() => setFilter(EMPTY_FILTER)}>clear filters</button>
            </div>
          )}
        </div>
      )}

      {!loading &&
        grouped.map(g => (
          <section key={g.label}>
            {g.label !== "_root" && (
              <div className="section-head" style={{ paddingTop: 16 }}>
                <div>
                  <span className="super">{g.sub ?? "GROUP"}</span>
                  <h2 style={{ fontSize: 16 }}>{g.label}</h2>
                </div>
                <div className="lcd" style={{ fontSize: 11 }}>
                  {g.items.length.toString().padStart(2, "0")} artists
                </div>
              </div>
            )}
            <div className="grid" data-testid="artist-grid">
              {g.items.map(item => (
                <ArtistCard
                  key={`${item.artist.id}-${item.displaySet?.id ?? "x"}`}
                  artist={item.artist}
                  displaySet={item.displaySet}
                  stages={stages}
                  rating={prefs[item.artist.slug] ?? null}
                  onOpen={onOpen}
                />
              ))}
            </div>
          </section>
        ))}

      <FilterSheet
        open={sheetOpen}
        stages={stages}
        genres={genres}
        value={filter}
        eventStartDate={eventStartDate ?? null}
        eventDays={maxDay}
        onChange={setFilter}
        onClose={() => setSheetOpen(false)}
      />
    </div>
  );
}

function ArtistCard({
  artist,
  displaySet,
  stages,
  rating,
  onOpen,
}: {
  artist: ArtistDTO;
  /** Which set's day/time/stage the card should show. Falls back to the
   *  artist's first set when the caller doesn't pin one. */
  displaySet?: SetDTO | null;
  stages: StageDTO[];
  rating: Rating | null;
  onOpen: (slug: string) => void;
}) {
  const shownSet = displaySet ?? artist.sets[0] ?? null;
  const stageInfo = shownSet?.stage;
  const color = stageInfo?.color ?? "#ff6a13";
  const stage = stages.find(s => s.slug === stageInfo?.slug);
  const ratingLetter =
    rating === "superlike"
      ? "★"
      : rating === "like"
        ? "♥"
        : rating === "meh"
          ? "?"
          : rating === "pass"
            ? "✕"
            : null;
  const initial = artist.name.replace(/[^A-Za-z0-9]/g, "").charAt(0).toUpperCase() || "?";
  return (
    <button
      className="artist-card"
      data-testid="artist-card"
      data-slug={artist.slug}
      style={{ ["--glow" as never]: `${color}80` }}
      onClick={() => onOpen(artist.slug)}
    >
      {artist.photoUrl ? (
        <div
          className="ac-photo"
          style={{ backgroundImage: `url(${artist.photoUrl})` }}
          aria-hidden="true"
        />
      ) : (
        <div className="ac-photo no-photo" aria-hidden="true">{initial}</div>
      )}
      <div className="ac-stage-glow" />
      <div className="ac-glow" />
      <div className="ac-stage" style={{ color }}>{stage?.name ?? ""}</div>
      {ratingLetter && (
        <div className={`ac-rating rating-${rating}`}>{ratingLetter}</div>
      )}
      <div className="ac-meta">
        <div className="ac-name">{artist.name}</div>
        <div className="ac-genre">{artist.genre}</div>
        {shownSet && (
          <div className="ac-time">
            D{shownSet.day}
            {shownSet.startLabel ? ` · ${shownSet.startLabel}` : ""}
          </div>
        )}
      </div>
    </button>
  );
}

type GroupItem = { artist: ArtistDTO; displaySet: SetDTO | null };
type Group = { label: string; sub?: string; items: GroupItem[] };

type SortGroupOpts = {
  sort: SortKey;
  group: GroupKey;
  stages: StageDTO[];
  eventStartDate: string | null;
  filterDays: number[];
  filterStages: string[];
};

/**
 * Build the display groups. The shape per item is `{artist, displaySet}` so
 * the same artist can appear in multiple groups (e.g. one card per day they
 * play) with the right time on each card.
 *
 * Group key decides which sections we render; sort key orders items inside
 * each section. When sorting by time within a group, we use the relevant
 * set for that group (not just the artist's earliest one).
 */
function sortAndGroup(
  artists: ArtistDTO[],
  { sort, group, stages, eventStartDate, filterDays, filterStages }: SortGroupOpts,
): Group[] {
  const dayFilterSet = filterDays.length ? new Set(filterDays) : null;
  const stageFilterSet = filterStages.length ? new Set(filterStages) : null;

  // Pick the most-relevant set for an artist given the active filters,
  // ignoring any group-specific dimension. Used as the card's
  // displaySet outside of day/stage groupings.
  const primarySetFor = (a: ArtistDTO): SetDTO | null => {
    const candidates = a.sets.filter(
      s =>
        (!dayFilterSet || dayFilterSet.has(s.day)) &&
        (!stageFilterSet || stageFilterSet.has(s.stage.slug)),
    );
    const pool = candidates.length ? candidates : a.sets;
    return pool.length ? earliestSet(pool) : null;
  };

  const compareItems = (a: GroupItem, b: GroupItem): number => {
    if (sort === "time") {
      // Items without a real published time sink to the bottom but stay
      // alpha-sorted among themselves.
      const aReal = a.displaySet && a.displaySet.end > a.displaySet.start;
      const bReal = b.displaySet && b.displaySet.end > b.displaySet.start;
      if (aReal && !bReal) return -1;
      if (!aReal && bReal) return 1;
      if (aReal && bReal) {
        const ad = a.displaySet!;
        const bd = b.displaySet!;
        if (ad.day !== bd.day) return ad.day - bd.day;
        if (ad.start !== bd.start) return ad.start - bd.start;
      }
      return a.artist.name.localeCompare(b.artist.name);
    }
    if (sort === "genre") {
      const ag = a.artist.genre ?? "zzz";
      const bg = b.artist.genre ?? "zzz";
      const c = ag.localeCompare(bg);
      if (c !== 0) return c;
      return a.artist.name.localeCompare(b.artist.name);
    }
    return a.artist.name.localeCompare(b.artist.name);
  };

  if (group === "none") {
    const items: GroupItem[] = artists.map(a => ({
      artist: a,
      displaySet: primarySetFor(a),
    }));
    items.sort(compareItems);
    return [{ label: "_root", items }];
  }

  if (group === "day") {
    // One section per day; an artist appears once per day they play. The
    // displaySet is *that day's* set (and respects the stage filter when
    // active so the surfaced time matches the user's narrowing).
    const byDay = new Map<number, GroupItem[]>();
    for (const a of artists) {
      const sets = a.sets.filter(
        s =>
          (!dayFilterSet || dayFilterSet.has(s.day)) &&
          (!stageFilterSet || stageFilterSet.has(s.stage.slug)),
      );
      // Group an artist's same-day sets so the card shows their earliest
      // slot of the day instead of duplicating one card per set.
      const seenDays = new Map<number, SetDTO>();
      for (const s of sets) {
        const cur = seenDays.get(s.day);
        if (!cur || s.start < cur.start) seenDays.set(s.day, s);
      }
      for (const [d, s] of seenDays.entries()) {
        const arr = byDay.get(d) ?? byDay.set(d, []).get(d)!;
        arr.push({ artist: a, displaySet: s });
      }
    }
    const labels = eventStartDate ? dayLabels(eventStartDate, 14, { dot: false }) : null;
    return Array.from(byDay.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([d, items]) => {
        items.sort(compareItems);
        return {
          label: labels?.[d - 1] ? `Day ${d} — ${labels[d - 1]}` : `Day ${d}`,
          sub: "DAY",
          items,
        };
      });
  }

  if (group === "stage") {
    // One section per stage; an artist appears once per stage they play.
    const byStage = new Map<string, GroupItem[]>();
    for (const a of artists) {
      const sets = a.sets.filter(
        s =>
          (!dayFilterSet || dayFilterSet.has(s.day)) &&
          (!stageFilterSet || stageFilterSet.has(s.stage.slug)),
      );
      const seenStages = new Map<string, SetDTO>();
      for (const s of sets) {
        const cur = seenStages.get(s.stage.slug);
        if (!cur || s.day < cur.day || (s.day === cur.day && s.start < cur.start)) {
          seenStages.set(s.stage.slug, s);
        }
      }
      for (const [stageSlug, s] of seenStages.entries()) {
        const arr = byStage.get(stageSlug) ?? byStage.set(stageSlug, []).get(stageSlug)!;
        arr.push({ artist: a, displaySet: s });
      }
    }
    return stages
      .filter(s => byStage.has(s.slug))
      .map(s => {
        const items = byStage.get(s.slug)!;
        items.sort(compareItems);
        return { label: s.name, sub: "STAGE", items };
      });
  }

  // group === "genre"
  const byGenre = new Map<string, GroupItem[]>();
  for (const a of artists) {
    const key = a.genre ?? "unknown";
    const arr = byGenre.get(key) ?? byGenre.set(key, []).get(key)!;
    arr.push({ artist: a, displaySet: primarySetFor(a) });
  }
  return Array.from(byGenre.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([label, items]) => {
      items.sort(compareItems);
      return { label, sub: "GENRE", items };
    });
}

function earliestSet(sets: SetDTO[]): SetDTO {
  let best = sets[0]!;
  for (const s of sets) {
    if (s.day < best.day || (s.day === best.day && s.start < best.start)) best = s;
  }
  return best;
}

/** Compact uppercase date range, e.g. "MAY 15–17", "APR 24 – MAY 2". */
function shortDateRange(startIso: string, endIso: string): string | null {
  const parts1 = startIso.split("-").map(Number);
  const parts2 = endIso.split("-").map(Number);
  if (parts1.length !== 3 || parts2.length !== 3) return null;
  if (parts1.some(n => !Number.isFinite(n)) || parts2.some(n => !Number.isFinite(n))) return null;
  const MONTHS = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  const mo1 = MONTHS[parts1[1]! - 1] ?? "";
  const mo2 = MONTHS[parts2[1]! - 1] ?? "";
  const d1 = parts1[2]!;
  const d2 = parts2[2]!;
  if (mo1 === mo2 && d1 === d2) return `${mo1} ${d1}`;
  if (mo1 === mo2) return `${mo1} ${d1}–${d2}`;
  return `${mo1} ${d1} – ${mo2} ${d2}`;
}
